import React, { useState, useEffect, useContext } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import ActorForm from './ActorForm';
import DirectorForm from './DirectorForm';
import CrewMemberForm from './CrewMemberForm';
import { SignUp } from '../../api/axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Alert from '../../components/common/Alert';
import {
  validateField,
  validateForm,
  checkAllRequiredFieldsFilled,
} from './validation';
import PrivacyPolicyPopup from '../../components/agreement/privacyPolicy/PrivacyPolicy';
import TermOfUsePopup from '../../components/agreement/termOfUse/TermOfUse';
import LoginPopup from '../../components/auth/LoginPopup';
import actorImage from '../../assets/actorImage.jpg';
import directorImage from '../../assets/directorImage.jpg';
import crewMemberImage from '../../assets/crewMemberImage.jpg';
import './registraton.css';
import ImageUpload from './profileImgUpload';

import OtpVerification from './OtpVerification';

import { AuthContext } from '../../context/AuthContext';

const Registration = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [tabValue, setTabValue] = useState(
    location.state?.role == 'actor' ? 0 : location.state?.role == 'castingdirectors' ? 1 : 2
  );
  const [agreementError, setAgreementError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    zipCode: '',
    location: '',
    email: location.state?.unverified ? location.state.email : '',
    phoneNumber: '',
    gender: '',
    birthday: '',
    ethnicity: '',
    age: '',
    password: '',
    confirmPassword: '',
    selectedRole: '',
    agreement: '',
    image: '',
    // images: [],
  });
  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [termOfUseOpen, setTermOfUseOpen] = useState(false);
  const [touchedFields, setTouchedFields] = useState({
    firstName: false,
    lastName: false,
    zipCode: false,
    location: false,
    email: false,
    phoneNumber: false,
    gender: false,
    birthday: false,
    ethnicity: false,
    age: false,
    password: false,
    confirmPassword: false,
    selectedRole: false,
    agreement: false,
    image: false,
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(location.state?.unverified || false);
  const [registeredUserId, setRegisteredUserId] = useState(null);

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
    setFormData({
      firstName: '',
      lastName: '',
      zipCode: '',
      location: '',
      email: '',
      phoneNumber: '',
      gender: '',
      birthday: '',
      ethnicity: '',
      age: '',
      password: '',
      confirmPassword: '',
      selectedRole: '',
      Image: '',
      agreement: false,
    });
    setErrors({});
    setTouchedFields({});
    setIsFormValid(false);
    setIsSubmitting(false);
    setShowOtpVerification(false); // Reset OTP verification visibility when tabs change
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: fieldValue,
    }));

    if (name === 'birthday') {
      const age = calculateAge(value);
      setFormData((prev) => ({ ...prev, age }));
    }

    setTouchedFields((prevTouchedFields) => ({
      ...prevTouchedFields,
      [name]: true,
    }));

    const fieldErrors = validateField(
      name,
      fieldValue,
      { ...formData, [name]: fieldValue },
      tabValue,
      touchedFields
    );

    setErrors((prevErrors) => ({
      ...prevErrors,
      ...fieldErrors,
    }));

    const newFormData = { ...formData, [name]: fieldValue };
    const formErrors = validateForm(newFormData, tabValue);
    const allFieldsFilled = checkAllRequiredFieldsFilled(newFormData);
    const hasErrors = Object.keys(formErrors).length > 0;

    setIsFormValid(allFieldsFilled && !hasErrors);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    const newErrors = validateField(
      name,
      value,
      formData,
      tabValue,
      touchedFields
    );
    setErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
  };

  const calculateAge = (birthday) => {
    const birthDate = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAgreementError('');

    // console.log('Form data submitted:', formData);

    const formErrors = validateForm(formData, tabValue, touchedFields);
    setErrors(formErrors);

    const allFieldsFilled = checkAllRequiredFieldsFilled(formData);
    // console.log('Validation data:', allFieldsFilled, formErrors);

    if (formErrors.agreement) {
      setAgreementError(formErrors.agreement);
    }

    const hasErrors = Object.keys(formErrors).length > 0;

    if (!allFieldsFilled || hasErrors) {
      setIsFormValid(false);
      setIsSubmitting(false);
      return;
    }

    setIsFormValid(true);

    const formDataToSend = new FormData();
    formDataToSend.append('email', formData.email);
    formDataToSend.append('password', formData.password);
    formDataToSend.append('firstName', formData.firstName);
    formDataToSend.append('lastName', formData.lastName);
    formDataToSend.append('zipCode', formData.zipCode);
    formDataToSend.append('location', formData.location);
    formDataToSend.append('gender', formData.gender);
    formDataToSend.append('contactNumber', formData.phoneNumber);
    formDataToSend.append('birthday', formData.birthday);
    formDataToSend.append('ethnicity', formData.ethnicity);

    if (formData.image) {
      formDataToSend.append("image", formData.image);
    }
    // if (formData.images && formData.images.length > 0) {
    //   formData.images.forEach((image, index) => {
    //     formDataToSend.append('images', image);
    //   });
    // }

    const role =
      tabValue === 0
        ? 'actor'
        : tabValue === 1
        ? formData.selectedRole === 'director'
          ? 'director'
          : 'producer'
        : 'crew';

    formDataToSend.append('role', role);

    // console.log('Sending formData:', formDataToSend);

    try {
      const res = await SignUp(formDataToSend);

      console.log('====================',res)

      if (res.success) {
        setOpenSnackbar(true);
        setAlertSeverity('success');
        setAlertMessage('Registration successful! Please verify your email with the OTP sent to your inbox.');
        setRegisteredUserId(res.userId);
        setShowOtpVerification(true);
      } else {
        throw new Error(res.message);
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Something went wrong';
      setAlertMessage(errorMessage);
      setAlertSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

    //Profile img upload
    const addImage = (croppedImage) => {
      setFormData((prevFormData) => ({
        ...prevFormData,
        image: croppedImage, 
      }));
    };

  const getBulletPoints = () => {
    switch (tabValue) {
      case 0:
        return (
          <ul className='Registrationlist'>
            <li className='Registrationlist-item'>
              Apply to roles matching your unique talents
            </li>
            <li className='Registrationlist-item'>
              Create a professional profile to showcase your skills
            </li>
          </ul>
        );
      case 1:
        return (
          <ul className='Registrationlist'>
            <li className='Registrationlist-item'>Free to cast a project</li>
            <li className='Registrationlist-item'>
              Easy to find the right talent
            </li>
            <li className='Registrationlist-item'>
              Simple to navigate the virtual casting office
            </li>
            <li className='Registrationlist-item'>
              Quick to reach 2+ Million diverse talent
            </li>
          </ul>
        );
      case 2:
        return (
          <ul className='Registrationlist'>
            <li className='Registrationlist-item'>
              Network with industry professionals
            </li>
            <li className='Registrationlist-item'>
              Showcase your skills and experience
            </li>
            <li className='Registrationlist-item'>
              Get notified about relevant job openings
            </li>
          </ul>
        );
      default:
        return null;
    }
  };

  const getBackgroundImage = (tabValue) => {
    switch (tabValue) {
      case 0:
        return `url(${actorImage})`;
      case 1:
        return `url(${directorImage})`;
      case 2:
        return `url(${crewMemberImage})`;
      default:
        return null;
    }
  };

  const openPrivacyPolicy = (e) => {
    e.preventDefault();
    setPrivacyPolicyOpen(true);
  };

  const openTermOfUse = (e) => {
    e.preventDefault();
    setTermOfUseOpen(true);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Box className='registration-container'>
      <Box
        className='left-panel'
        sx={{
          backgroundImage: getBackgroundImage(tabValue),
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',

          '& .MuiBox-root': {
            borderRadius: '15px 0 0 15px',
          },
        }}
      >
        <Typography
          variant='h3'
          className='page-title'
          style={{ fontSize: '2.5rem', fontWeight: 600 }}
        >
          Sign Up With Open Curtains Casting
        </Typography>
        {getBulletPoints()}
        <Typography>
          Already a member?
          <span className='login-link' onClick={() => setIsLoginOpen(true)}>
            {'  '}
            Log in
          </span>
        </Typography>
      </Box>
      <Box className='right-panel'>
        <Tabs
          value={tabValue}
          onChange={handleChange}
          className='tab-container'
          sx={{
            '& .MuiTab-root': {
              fontSize: {
                xs: '0.5rem',
                sm: '0.5rem',
                md: '0.6rem',
                lg: '0.7rem',
              },
              minWidth: {
                xs: '60px',
                sm: '80px',
                md: '100px',
              },
              padding: {
                xs: '4px 6px',
                sm: '4px 8px',
              },
              color: '#6E0506',
              '&:hover': {
                color: '#E54447',
              },
            },
            '& .Mui-selected': {
              color: '#E54447',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#E54447',
            },
          }}
        >
          <Tab label='ACTOR' />
          <Tab label='CASTING DIRECTOR' />
          <Tab label='CREW MEMBER' />
        </Tabs>
        {showOtpVerification ? (
          <OtpVerification
            email={location.state?.email || formData.email}
            registeredUserId={registeredUserId}
            tabValue={tabValue}
          />
        ) : (
          <form onSubmit={handleSubmit}>
            {tabValue === 0 && (
              <ActorForm
                formData={formData}
                handleInputChange={handleInputChange}
                handleBlur={handleBlur}
                errors={errors}
              />
            )}

            {tabValue === 1 && (
              <DirectorForm
                formData={formData}
                handleInputChange={handleInputChange}
                handleBlur={handleBlur}
                errors={errors}
              />
            )}
            {tabValue === 2 && (
              <CrewMemberForm
                formData={formData}
                handleInputChange={handleInputChange}
                handleBlur={handleBlur}
                errors={errors}
              />
            )}
            <ImageUpload onImageAdd={addImage} errors={errors} />
            <FormControlLabel
              className='terms-checkbox'
              control={
                <Checkbox
                  name='agreement'
                  checked={formData.agreement}
                  onChange={handleInputChange}
                />
              }
              label={
                <Typography className='checkbox-label'>
                  By choosing to join, I certify I am at least 18 years old and
                  have read and agree to the Open Curtains Casting.{' '}
                  <span
                    className='highlight'
                    onClick={openPrivacyPolicy}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    privacy policy
                  </span>{' '}
                  and{' '}
                  <span
                    className='highlight'
                    onClick={openTermOfUse}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    terms of use
                  </span>
                  . I agree to receive welcome email, newsletter, SMS & occasional
                  account updates from Open Curtains Casting
                </Typography>
              }
              sx={{
                marginLeft: '0px',
                marginRight: '0px',
                borderRadius: '5px',
                alignItems: 'flex-start',
                '& .MuiTypography-root': {
                  fontSize: '0.875rem',
                  color: '#333',
                },
                '& .MuiCheckbox-root': {
                  color: '#f48fb1',
                },
              }}
            />
            <Typography>
              <span className='error'>{agreementError}</span>
            </Typography>
            <PrivacyPolicyPopup
              open={privacyPolicyOpen}
              onClose={() => setPrivacyPolicyOpen(false)}
            />

            <TermOfUsePopup
              open={termOfUseOpen}
              onClose={() => setTermOfUseOpen(false)}
            />

            <Button
              type='submit'
              fullWidth
              variant='contained'
              sx={{
                mt: 3,
                mb: 2,
                minHeight: 44,
                backgroundColor: '#6E0506 !important',
                '&:hover': {
                  backgroundColor: '#6E0506 !important',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#E54447 !important',
                  color: '#fff',
                },
                color: '#fff',
              }}
              disabled={isSubmitting || showOtpVerification}
            >
              {isSubmitting ? (
                <CircularProgress size={22} color='inherit' />
              ) : (
                'Sign Up'
              )}
            </Button>
          </form>
        )}
      </Box>

      <LoginPopup
        isLoginOpen={isLoginOpen}
        setIsLoginOpen={setIsLoginOpen}
        redirectTo={location.state?.from}
      />
      <Alert
        openSnackbar={openSnackbar}
        alertSeverity={alertSeverity}
        setOpenSnackbar={setOpenSnackbar}
        alertMessage={alertMessage}
      />
    </Box>
  );
};

export default Registration;

